import React, { useEffect, useRef, type ComponentType } from 'react';
import { xcss, Box } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	type ActivitySortOrderType,
	NEWEST_FIRST,
} from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useShouldUseRelayIssueHistory } from '@atlassian/jira-issue-context-service/src/main.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import LoadingButton from '../LoadingButton.tsx';
import type { HistoryItem } from '../../types.tsx';
import HistoryItemComponent from './history-item/index.tsx';
import messages from './messages.tsx';

const HISTORY_GROUPED_ITEMS_CATEGORY = 'HISTORY' as const;

// Error boundary wrapper for individual history items
const HistoryItemWithErrorBoundary = ({ historyItem }: { historyItem: HistoryItem }) => {
	const handleError = (location: string, error: Error) => {
		log.safeErrorWithoutCustomerData(
			'issue-history.history-item-render-error',
			'Failed to render individual history item',
			{
				error: error?.message,
				errorStack: error?.stack,
				componentStack: location,
				historyItemType: historyItem?.type,
			},
		);
	};
	return (
		<ErrorBoundary packageName="issue-history" onError={handleError}>
			<HistoryItemComponent historyItem={historyItem} />
		</ErrorBoundary>
	);
};

export type Props = {
	isLoadingMore: boolean;
	hasMore: boolean;
	history: HistoryItem[];
	sortOrder: ActivitySortOrderType;
	onLoadMore: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	GroupedActivityEntryComponent?: ComponentType<Record<any, any>>;
};

export const LoadMoreButton = ({
	formatMessage,
	isLoadingMore,
	onLoadMore,
	sortOrder,
}: {
	formatMessage: FormatMessage;
	isLoadingMore: boolean;
	sortOrder: ActivitySortOrderType;
	onLoadMore: () => void;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const prevIsLoadingMore = useRef(isLoadingMore);

	useEffect(() => {
		prevIsLoadingMore.current = isLoadingMore;
	}, [isLoadingMore]);

	const onLoadMoreButtonClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'loadMoreButtonClicked', {
			loadMoreButtonTypeClicked:
				sortOrder === NEWEST_FIRST ? 'LOAD_OLDER_BUTTON' : 'LOAD_NEWER_BUTTON',
		});
		onLoadMore();
	};

	return (
		<LoadingButton
			shouldFitContainer
			isDisabled={isLoadingMore}
			isLoading={isLoadingMore}
			onClick={onLoadMoreButtonClick}
		>
			{formatMessage(messages.loadMore)}
		</LoadingButton>
	);
};

const HistoryItems = ({
	history,
	isLoadingMore,
	onLoadMore,
	hasMore,
	sortOrder,
	GroupedActivityEntryComponent: GroupedActivityEntry,
}: Props): React.JSX.Element => {
	const intl = useIntl();
	const shouldUseRelayIssueHistory = useShouldUseRelayIssueHistory();

	return (
		<ContextualAnalyticsData sourceName="historyItems" sourceType={SCREEN}>
			{history.map((historyItem, index) => {
				const isGroupedActivityEntryEnrolledInIssueViewActivityTimelineExperiment =
					// eslint-disable-next-line jira/ff/no-preconditioning
					expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false) &&
					historyItem?.type === HISTORY_GROUPED_ITEMS_CATEGORY &&
					GroupedActivityEntry;

				if (isGroupedActivityEntryEnrolledInIssueViewActivityTimelineExperiment) {
					return <GroupedActivityEntry key={index} item={historyItem} />;
				}
				if (expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)) {
					return (
						<React.Fragment key={index}>
							<HistoryItemComponent historyItem={historyItem} />
							<Box xcss={horizontalDividerStyles} />
						</React.Fragment>
					);
				}
				return shouldUseRelayIssueHistory ? (
					<HistoryItemWithErrorBoundary key={index} historyItem={historyItem} />
				) : (
					<HistoryItemComponent key={index} historyItem={historyItem} />
				);
			})}
			{hasMore && (
				<div data-testid="issue-history.ui.history-items.load-more-button">
					<LoadMoreButton
						formatMessage={intl.formatMessage}
						isLoadingMore={isLoadingMore}
						onLoadMore={onLoadMore}
						sortOrder={sortOrder}
					/>
				</div>
			)}
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const horizontalDividerStyles = xcss({
	height: '1px',
	width: '100%',
	backgroundColor: 'color.background.neutral',
	marginTop: 'space.200',
});

export default HistoryItems;
